import { useCallback, useEffect, useRef, useState } from 'react';

const KEY = 'admin.sidebar.collapsed';

const read = (): boolean => {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

/** Desktop sidebar collapsed to an icon rail, persisted per browser. */
export function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(read);
  useEffect(() => {
    try {
      if (collapsed) localStorage.setItem(KEY, '1');
      else localStorage.removeItem(KEY);
    } catch {
      /* private mode */
    }
  }, [collapsed]);
  const toggle = useCallback(() => {
    setCollapsed((c) => !c);
  }, []);
  return [collapsed, toggle];
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keep Tab inside `root` while it is open; Esc calls `onClose`. */
export function trapFocus(root: HTMLElement, onClose: () => void): () => void {
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const els = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
    const first = els[0];
    const last = els.at(-1);
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !root.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !root.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', onKey);
  return () => {
    document.removeEventListener('keydown', onKey);
  };
}

/**
 * The narrow-screen drawer: closes on navigation, traps focus while open and gives focus back to
 * the menu button on close.
 */
export function useNavDrawer(routeKey: string) {
  const [drawer, setDrawer] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  // Close the drawer on navigation; trap focus while it is open and restore it on close.
  const [drawerRoute, setDrawerRoute] = useState(routeKey);
  if (drawerRoute !== routeKey) {
    setDrawerRoute(routeKey);
    setDrawer(false);
  }
  useEffect(() => {
    const root = drawerRef.current;
    if (!drawer || !root) return;
    root.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    const button = menuButton.current;
    const release = trapFocus(root, () => {
      setDrawer(false);
    });
    return () => {
      release();
      button?.focus();
    };
  }, [drawer]);
  const open = () => {
    setDrawer(true);
  };
  const close = () => {
    setDrawer(false);
  };
  return { drawer, open, close, drawerRef, menuButton };
}
